// ─── AppHeaderShell ───────────────────────────────────────────────────────────
// THE single source of truth for the white header strip (Logo · Dashboard /
// Data Analytics / Report / Gallery / Settings / Live · As of <time>).
//
// Why this exists: the header was implemented TWICE — inline in DashboardPage
// and again in AppHeader for every non-dashboard page (Message Center,
// Account, Help Center, Plans, Stripe Settings, CMS…). The two copies drifted,
// so /messages rendered a 60px-tall strip with a 40px centred logo while
// /dashboard rendered a 100px strip with a 70px right-aligned nav. This
// component owns the frame and styling; both callers now render it, so the
// two can no longer diverge.
//
// Deliberately presentational: it owns LAYOUT ONLY and takes behaviour as
// props. The dashboard's header drives in-page state (activeTab, report
// mode, Live keyboard navigation…) while AppHeader's navigates by route —
// keeping that logic with each caller is what makes sharing the frame
// risk-free.
//
// `renderDropdown` is always invoked (never gated on `openTab`) because each
// caller has its own visibility rule — the dashboard's Live menu, for one,
// also opens on keyboard focus, not just hover.
// ──────────────────────────────────────────────────────────────────────────────

import { ChevronDown } from 'lucide-react'

export const HEADER_TABS = ['Dashboard', 'Data Analytics', 'Report', 'Gallery', 'Settings', 'Live']

// Gallery is the only tab with no submenu — a click navigates straight there.
export const HEADER_TABS_WITH_DROPDOWN = new Set(['Dashboard', 'Data Analytics', 'Report', 'Settings', 'Live'])

export function AppHeaderShell({
  activeTab = null,
  // Which tab's menu is currently open — drives the chevron rotation only.
  openTab = null,
  onTabEnter = () => {},
  onTabLeave = () => {},
  onTabClick = () => {},
  // (tab) => JSX | null — the caller's own dropdown, positioned by the
  // relative wrapper this component provides.
  renderDropdown = () => null,
  // (tab) => object — extra props spread onto the tab <button>, e.g. the
  // dashboard's data-tab-name / onFocus / onKeyDown for Live keyboard nav.
  tabButtonProps = () => ({}),
  nowLabel = '',
  onRefresh = () => window.location.reload(),
  // Header logo. Callers that are scoped to a project (the dashboard) pass the
  // project's own logo here; everything else falls back to the generic WQData
  // LIVE app logo. Never hardcode a specific customer's logo in this shared shell.
  logoSrc = '/images/logo.png',
}) {
  return (
    // Desktop/tablet only. Below 768px MobileNavBar is the header — rendering
    // both stacked a second logo and the six-tab row under the mobile bar,
    // which is what the XD mobile frames replace with the hamburger.
    <div className="hidden md:block bg-white border-b border-gray-200 shadow-md">
      <div className="wq-head flex items-center justify-between px-6 pt-3 h-[100px]">
        {/* Logo — h-[70px] is the logo's intrinsic height. It replaces a
            previous `h-15`, which is NOT a Tailwind class: it silently did
            nothing, so the logo fell back to its natural size. Spelling the
            real value out keeps the approved rendering while making the
            height explicit rather than accidental. */}
        <div className="flex items-center flex-shrink-0 pb-3">
          <img
            src={logoSrc}
            alt="Project logo"
            className="h-[70px] w-auto object-contain"
            onError={(e) => {
              // If a project's logo URL 404s, fall back to the app logo rather
              // than showing a broken image.
              if (e.currentTarget.src.indexOf('/images/logo.png') === -1) {
                e.currentTarget.src = '/images/logo.png'
              }
            }}
          />
        </div>

        {/* Navigation tabs */}
        <div className="flex items-end gap-12 flex-1 justify-end self-stretch pr-9">
          {HEADER_TABS.map((tab) => {
            const hasDropdown = HEADER_TABS_WITH_DROPDOWN.has(tab)
            return (
              <div
                key={tab}
                className="relative h-full flex items-end"
                onMouseEnter={() => onTabEnter(tab)}
                onMouseLeave={() => onTabLeave(tab)}
              >
                <button
                  onClick={() => onTabClick(tab)}
                  {...tabButtonProps(tab)}
                  // border-b-4 is always present so the row never shifts when a
                  // tab becomes active — only the COLOUR changes.
                  className={`common-size15 border-b-4 text-sm font-bold pb-3 transition-colors flex items-center gap-1 whitespace-nowrap ${
                    activeTab === tab
                      ? 'border-[#358ECD] text-[#4D4D4D]'
                      : 'border-transparent text-[#4D4D4D] hover:text-[#4D4D4D] hover:border-[#358ECD]'
                  }`}
                >
                  {tab}
                  {hasDropdown && (
                    <ChevronDown className={`w-5 h-5 transition-transform ${openTab === tab ? 'rotate-180' : ''}`} />
                  )}
                </button>

                {renderDropdown(tab)}
              </div>
            )
          })}
        </div>

        {/* Right: "As of" timestamp + refresh */}
        <div className="flex items-end justify-end self-stretch pb-3">
          <span className="common-size15 text-sm text-[#4D4D4D] whitespace-nowrap">As of {nowLabel}</span>
          <button onClick={onRefresh} className="hover:opacity-70 transition p-1" title="Refresh">
            <img src="/images/ICON_Refresh.png" alt="Refresh" className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
